import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Zap, Trash2, Loader2, Activity } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

export default function Flows() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/flows");
    setFlows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name) return toast.error("Name required");
    const { data } = await api.post("/flows", {
      name, description,
      nodes: [
        { id: "n1", type: "trigger_new_contact", position: { x: 100, y: 100 }, data: {} },
        { id: "n2", type: "action_send_message", position: { x: 100, y: 250 }, data: { message: "Hi {{name}}, welcome!" } },
        { id: "n3", type: "action_end", position: { x: 100, y: 400 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "n1", target: "n2" },
        { id: "e2", source: "n2", target: "n3" },
      ],
    });
    setOpen(false);
    setName(""); setDescription("");
    navigate(`/flows/${data.id}`);
  };

  const toggleActive = async (flow) => {
    await api.patch(`/flows/${flow.id}`, { active: !flow.active });
    load();
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete flow?",
      description: "The flow and all its runs will be removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/flows/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="flows-page">
      <PageHeader
        title="Flows"
        description="Build no-code automation that responds to triggers"
        actions={
          <Button onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-dark" data-testid="flow-new-button">
            <Plus className="w-4 h-4 mr-1.5" />New flow
          </Button>
        }
      />

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      ) : flows.length === 0 ? (
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-12 text-center">
            <Zap className="w-10 h-10 text-slate-300 mx-auto" />
            <div className="mt-3 font-heading text-lg font-semibold text-slate-700">No flows yet</div>
            <p className="text-sm text-slate-500 mt-1">Build your first automation</p>
            <Button onClick={() => setOpen(true)} className="mt-4 bg-brand hover:bg-brand-dark">
              <Plus className="w-4 h-4 mr-1.5" />New flow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {flows.map((f) => (
            <Card key={f.id} className="border-slate-200 shadow-none" data-testid={`flow-card-${f.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-md bg-violet-50 text-violet-600 grid place-items-center">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div>
                      <Link to={`/flows/${f.id}`} className="font-heading font-semibold text-slate-900 hover:text-brand-dark">
                        {f.name}
                      </Link>
                      <div className="text-xs text-slate-500">{(f.nodes || []).length} nodes</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!f.active}
                      onCheckedChange={() => toggleActive(f)}
                      data-testid={`flow-toggle-${f.id}`}
                    />
                    <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {f.description && <p className="text-sm text-slate-500 mt-3 line-clamp-2">{f.description}</p>}
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${f.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {f.active ? "Active" : "Draft"}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New flow</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="flow-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-brand hover:bg-brand-dark" data-testid="flow-create-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
